import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../layouts/Navbar";
import Footer from "../../layouts/Footer";
import Sidebar from "../../layouts/Sidebar";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UpdateQuestion = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: "",
        category_id: "",
        sub_category_id: "",
        year: "",
        description: "",
        question_pdf: null,
        answer_pdf: null,
        current_question_file: "",
        current_answer_file: ""
    });
    
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState({
        question_pdf: null,
        answer_pdf: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Generate years from 2000 to current year
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from(
        { length: currentYear - 2000 + 1 },
        (_, i) => (currentYear - i).toString()
    );

    // Fetch question details on component mount
    useEffect(() => {
        if (id) {
            fetchQuestionDetails();
            fetchCategories();
        } else {
            toast.error("Invalid question ID");
            navigate("/admin/questions");
        }
    }, [id]);

    const fetchQuestionDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `https://pastq.eddcodesgh.com/qApi/admin/questions/single_question.php?id=${id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success" && response.data.records[0]) {
                const question = response.data.records[0];
                
                setFormData({
                    title: question.title || question.question_title || "",
                    category_id: question.category_id || question.cat_id || "",
                    sub_category_id: question.sub_category_id || question.sub_cat_id || "",
                    year: question.year || "",
                    description: question.description || question.question_description || "",
                    question_pdf: null,
                    answer_pdf: null,
                    current_question_file: question.question_file || "",
                    current_answer_file: question.answer_file || ""
                });

                // Fetch sub-categories for this category
                if (question.category_id || question.cat_id) {
                    fetchSubCategories(question.category_id || question.cat_id);
                }
            } else {
                toast.error("Question not found");
                navigate("/admin/questions");
            }
        } catch (error) {
            toast.error("Failed to load question details");
            navigate("/admin/questions");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                "https://pastq.eddcodesgh.com/qApi/admin/categories/_viewcategories.php",
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                setCategories(response.data.records || []);
            } else {
                toast.error("Failed to load categories");
            }
        } catch (error) {
            toast.error("Network error loading categories");
            console.error("Error fetching categories:", error);
        } finally {
            setLoadingCategories(false);
        }
    };

    const fetchSubCategories = async (categoryId) => {
        try {
            const response = await axios.get(
                `https://pastq.eddcodesgh.com/qApi/admin/categories/subcategories.php?catId=${categoryId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                setSubCategories(response.data.records || []);
            }
        } catch (error) {
            console.error("Error fetching sub-categories:", error);
            setSubCategories([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // If category changes, clear sub_category_id and fetch new sub-categories
        if (name === "category_id") {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
                sub_category_id: ""
            }));
            if (value) {
                fetchSubCategories(value);
            } else {
                setSubCategories([]);
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];
        
        if (file) {
            // Validate file type
            if (!file.type.includes("pdf")) {
                toast.error("Please upload PDF files only");
                return;
            }
            
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                toast.error("File size should be less than 10MB");
                return;
            }

            setFormData((prev) => ({
                ...prev,
                [name]: file,
            }));

            setSelectedFiles((prev) => ({
                ...prev,
                [name]: {
                    name: file.name,
                    size: (file.size / 1024 / 1024).toFixed(2) + " MB",
                    type: file.type,
                },
            }));
        }
    };

    const removeFile = (fieldName) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: null,
        }));

        setSelectedFiles((prev) => ({
            ...prev,
            [fieldName]: null,
        }));
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            toast.error("Question title is required");
            return false;
        }

        if (!formData.category_id) {
            toast.error("Please select a category");
            return false;
        }

        if (!formData.year) {
            toast.error("Please select a year");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsSubmitting(true);
        setUploadProgress(0);

        const formDataToSend = new FormData();
        formDataToSend.append("id", id);
        formDataToSend.append("title", formData.title);
        formDataToSend.append("category_id", formData.category_id);
        formDataToSend.append("sub_category_id", formData.sub_category_id || "");
        formDataToSend.append("year", formData.year);
        formDataToSend.append("description", formData.description || "");
        formDataToSend.append("current_question_file", formData.current_question_file);
        formDataToSend.append("current_answer_file", formData.current_answer_file);
        
        // Append files only if new ones are selected
        if (formData.question_pdf) {
            formDataToSend.append("question_pdf", formData.question_pdf);
        }
        if (formData.answer_pdf) {
            formDataToSend.append("answer_pdf", formData.answer_pdf);
        }

        try {
            const response = await axios.post(
                `https://pastq.eddcodesgh.com/qApi/admin/questions/update.php?id=${id}`,
                formDataToSend,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(progress);
                    },
                }
            );

            if (response.data.status === 200) {
                toast.success("Question updated successfully!");
                
                // Navigate back after success
                setTimeout(() => {
                    navigate("/admin/questions");
                }, 2000);
            } else {
                throw new Error(response.data.message || "Update failed");
            }
        } catch (error) {
            toast.error(error.message || "An error occurred while updating question");
        } finally {
            setIsSubmitting(false);
        }
    };

    const downloadCurrentFile = (filename, type) => {
        if (filename) {
            window.open(`https://pastq.eddcodesgh.com/qApi/uploads/questions/${filename}`, '_blank');
        } else {
            toast.warning(`No ${type} file available`);
        }
    };

    const deleteCurrentFile = async (fieldName) => {
        const fileType = fieldName === "current_question_file" ? "question" : "answer";
        
        if (window.confirm(`Are you sure you want to delete the current ${fileType} PDF?`)) {
            try {
                const response = await axios.post(
                    `https://pastq.eddcodesgh.com/qApi/admin/questions/delete-file.php`,
                    {
                        id: id,
                        field_name: fieldName.replace("current_", ""),
                        filename: formData[fieldName]
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                        },
                        withCredentials: true,
                    }
                );

                if (response.data.status === 200) {
                    toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} PDF deleted successfully!`);
                    
                    // Update form data
                    setFormData((prev) => ({
                        ...prev,
                        [fieldName]: ""
                    }));
                } else {
                    throw new Error(response.data.message || "Delete failed");
                }
            } catch (error) {
                toast.error(`Failed to delete ${fileType} PDF: ${error.message}`);
            }
        }
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <Sidebar />
                <main id="main" className="main">
                    <div className="pagetitle">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h1>Edit Question</h1>
                                <nav>
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item">
                                            <Link to="/dashboard">Dashboard</Link>
                                        </li>
                                        <li className="breadcrumb-item">
                                            <Link to="/admin/questions">Questions</Link>
                                        </li>
                                        <li className="breadcrumb-item active">Loading...</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>
                    <section className="section">
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3">Loading question details...</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            <Sidebar />
            
            <main id="main" className="main">
                <div className="pagetitle">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1>Edit Question</h1>
                            <nav>
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="/admin/questions">Questions</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Edit: {formData.title}</li>
                                </ol>
                            </nav>
                        </div>
                        <Link to="/admin/questions" className="btn btn-outline-primary">
                            <i className="bi bi-arrow-left me-2"></i>
                            Back to Questions
                        </Link>
                    </div>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h5 className="card-title mb-0">Edit Question Details</h5>
                                        <div className="badge bg-warning">
                                            <i className="bi bi-pencil-square me-1"></i>
                                            Editing
                                        </div>
                                    </div>

                                    <form onSubmit={handleSubmit}>
                                        {/* Basic Information */}
                                        <div className="row mb-4">
                                            <div className="col-md-8">
                                                <label className="form-label">
                                                    Question Title <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="title"
                                                    className="form-control"
                                                    placeholder="e.g., 2024 WASSCE Mathematics Paper 2"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <div className="form-text">
                                                    Update the descriptive title for this question set
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    Year <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    name="year"
                                                    className="form-select"
                                                    value={formData.year}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Year</option>
                                                    {yearOptions.map((year) => (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Category Selection */}
                                        <div className="row mb-4">
                                            <div className="col-md-6">
                                                <label className="form-label">
                                                    Exam Type <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    name="category_id"
                                                    className="form-select"
                                                    value={formData.category_id}
                                                    onChange={handleInputChange}
                                                    required
                                                    disabled={loadingCategories}
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories.map((category) => (
                                                        <option key={category.id || category.cat_id} value={category.id || category.cat_id}>
                                                            {category.title || category.cat_title} - {category.years}
                                                        </option>
                                                    ))}
                                                </select>
                                                {loadingCategories && (
                                                    <div className="form-text">
                                                        <small>Loading categories...</small>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">
                                                    Sub-category (Optional)
                                                </label>
                                                <select
                                                    name="sub_category_id"
                                                    className="form-select"
                                                    value={formData.sub_category_id}
                                                    onChange={handleInputChange}
                                                    disabled={!formData.category_id || subCategories.length === 0}
                                                >
                                                    <option value="">Select Sub-category</option>
                                                    {subCategories.map((sub) => (
                                                        <option key={sub.id || sub.year} value={sub.id || sub.year}>
                                                            Year {sub.year}
                                                        </option>
                                                    ))}
                                                </select>
                                                {formData.category_id && subCategories.length === 0 && (
                                                    <div className="form-text">
                                                        <small>No sub-categories available for this category</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Description (Optional)
                                            </label>
                                            <textarea
                                                name="description"
                                                className="form-control"
                                                rows="3"
                                                placeholder="Add any additional notes or instructions..."
                                                value={formData.description}
                                                onChange={handleInputChange}
                                            ></textarea>
                                        </div>

                                        {/* Current Files Section */}
                                        <div className="row mb-4">
                                            {/* Current Question PDF */}
                                            <div className="col-md-6">
                                                <div className="card border-primary">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h6 className="card-title mb-0 text-primary">
                                                                <i className="bi bi-file-earmark-text me-2"></i>
                                                                Current Question PDF
                                                            </h6>
                                                        </div>
                                                        
                                                        {formData.current_question_file ? (
                                                            <div className="current-file-info p-3 border rounded-3 bg-light">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div>
                                                                        <i className="bi bi-file-pdf text-danger fs-4"></i>
                                                                        <div className="ms-3 d-inline-block">
                                                                            <strong>{formData.current_question_file}</strong>
                                                                            <div className="text-muted small">
                                                                                Existing question file
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex gap-2">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => downloadCurrentFile(formData.current_question_file, 'Question')}
                                                                            title="Download"
                                                                        >
                                                                            <i className="bi bi-download"></i>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => deleteCurrentFile("current_question_file")}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center p-4 border rounded-3 bg-warning bg-opacity-10">
                                                                <i className="bi bi-exclamation-triangle text-warning fs-4"></i>
                                                                <p className="mt-2 mb-0 text-muted">No question PDF uploaded</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Current Answer PDF */}
                                            <div className="col-md-6">
                                                <div className="card border-success">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h6 className="card-title mb-0 text-success">
                                                                <i className="bi bi-file-earmark-check me-2"></i>
                                                                Current Answer PDF
                                                            </h6>
                                                        </div>
                                                        
                                                        {formData.current_answer_file ? (
                                                            <div className="current-file-info p-3 border rounded-3 bg-light">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div>
                                                                        <i className="bi bi-file-pdf text-success fs-4"></i>
                                                                        <div className="ms-3 d-inline-block">
                                                                            <strong>{formData.current_answer_file}</strong>
                                                                            <div className="text-muted small">
                                                                                Existing answer file
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex gap-2">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-success"
                                                                            onClick={() => downloadCurrentFile(formData.current_answer_file, 'Answer')}
                                                                            title="Download"
                                                                        >
                                                                            <i className="bi bi-download"></i>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => deleteCurrentFile("current_answer_file")}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center p-4 border rounded-3 bg-warning bg-opacity-10">
                                                                <i className="bi bi-exclamation-triangle text-warning fs-4"></i>
                                                                <p className="mt-2 mb-0 text-muted">No answer PDF uploaded</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Update Files Section */}
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <div className="alert alert-info">
                                                    <i className="bi bi-info-circle me-2"></i>
                                                    <strong>Note:</strong> Upload new files only if you want to replace the existing ones. Leave empty to keep current files.
                                                </div>
                                            </div>
                                        </div>

                                        {/* New Question PDF Upload */}
                                        <div className="row mb-4">
                                            <div className="col-md-6 mb-4">
                                                <div className="card border-primary">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h6 className="card-title mb-0 text-primary">
                                                                <i className="bi bi-cloud-arrow-up me-2"></i>
                                                                Update Question PDF (Optional)
                                                            </h6>
                                                        </div>
                                                        
                                                        <div className="file-upload-area">
                                                            {!selectedFiles.question_pdf ? (
                                                                <div className="file-dropzone text-center p-4 border rounded-3 bg-light">
                                                                    <i className="bi bi-cloud-upload display-4 text-muted mb-3"></i>
                                                                    <p className="mb-2">Drag & drop new question PDF here</p>
                                                                    <p className="text-muted small mb-3">or click to browse</p>
                                                                    <input
                                                                        type="file"
                                                                        name="question_pdf"
                                                                        className="form-control d-none"
                                                                        id="questionPdf"
                                                                        accept=".pdf"
                                                                        onChange={handleFileChange}
                                                                    />
                                                                    <label htmlFor="questionPdf" className="btn btn-outline-primary">
                                                                        <i className="bi bi-upload me-2"></i>
                                                                        Choose New File
                                                                    </label>
                                                                </div>
                                                            ) : (
                                                                <div className="file-preview p-3 border rounded-3 bg-success bg-opacity-10">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <i className="bi bi-file-pdf text-danger fs-4"></i>
                                                                            <div className="ms-3 d-inline-block">
                                                                                <strong>{selectedFiles.question_pdf.name}</strong>
                                                                                <div className="text-muted small">
                                                                                    {selectedFiles.question_pdf.size}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => removeFile("question_pdf")}
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div className="form-text mt-2">
                                                                Upload new question PDF to replace current file (max 10MB)
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* New Answer PDF Upload */}
                                            <div className="col-md-6 mb-4">
                                                <div className="card border-success">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h6 className="card-title mb-0 text-success">
                                                                <i className="bi bi-cloud-arrow-up me-2"></i>
                                                                Update Answer PDF (Optional)
                                                            </h6>
                                                        </div>
                                                        
                                                        <div className="file-upload-area">
                                                            {!selectedFiles.answer_pdf ? (
                                                                <div className="file-dropzone text-center p-4 border rounded-3 bg-light">
                                                                    <i className="bi bi-cloud-upload display-4 text-muted mb-3"></i>
                                                                    <p className="mb-2">Drag & drop new answer PDF here</p>
                                                                    <p className="text-muted small mb-3">or click to browse</p>
                                                                    <input
                                                                        type="file"
                                                                        name="answer_pdf"
                                                                        className="form-control d-none"
                                                                        id="answerPdf"
                                                                        accept=".pdf"
                                                                        onChange={handleFileChange}
                                                                    />
                                                                    <label htmlFor="answerPdf" className="btn btn-outline-success">
                                                                        <i className="bi bi-upload me-2"></i>
                                                                        Choose New File
                                                                    </label>
                                                                </div>
                                                            ) : (
                                                                <div className="file-preview p-3 border rounded-3 bg-success bg-opacity-10">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <i className="bi bi-file-pdf text-success fs-4"></i>
                                                                            <div className="ms-3 d-inline-block">
                                                                                <strong>{selectedFiles.answer_pdf.name}</strong>
                                                                                <div className="text-muted small">
                                                                                    {selectedFiles.answer_pdf.size}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => removeFile("answer_pdf")}
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div className="form-text mt-2">
                                                                Upload new answer PDF to replace current file (max 10MB)
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Upload Progress */}
                                        {uploadProgress > 0 && (
                                            <div className="mb-4">
                                                <div className="progress" style={{ height: "10px" }}>
                                                    <div
                                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                                        role="progressbar"
                                                        style={{ width: `${uploadProgress}%` }}
                                                        aria-valuenow={uploadProgress}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    >
                                                        {uploadProgress}%
                                                    </div>
                                                </div>
                                                <div className="text-center mt-2">
                                                    <small className="text-muted">
                                                        Uploading... {uploadProgress}% complete
                                                    </small>
                                                </div>
                                            </div>
                                        )}

                                        {/* Submit Buttons */}
                                        <div className="d-flex justify-content-between mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => navigate("/admin/questions")}
                                                disabled={isSubmitting}
                                            >
                                                <i className="bi bi-x-circle me-2"></i>
                                                Cancel
                                            </button>
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-4"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <span
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                                aria-hidden="true"
                                                            ></span>
                                                            Updating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-check-circle me-2"></i>
                                                            Update Question
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* Instructions Card */}
                            <div className="card mt-4">
                                <div className="card-body">
                                    <h6 className="card-title">
                                        <i className="bi bi-info-circle me-2 text-info"></i>
                                        Update Instructions
                                    </h6>
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Only upload new PDF files if you want to replace existing ones
                                        </li>
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Leave file uploads empty to keep current files
                                        </li>
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            You can delete current files using the trash icon
                                        </li>
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Required fields are marked with *
                                        </li>
                                        <li>
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            All changes are saved when you click "Update Question"
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <style jsx>{`
                .card {
                    border-radius: 12px;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
                }
                
                .file-dropzone {
                    border: 2px dashed #dee2e6;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                
                .file-dropzone:hover {
                    border-color: #0d6efd;
                    background-color: rgba(13, 110, 253, 0.05);
                }
                
                .file-preview {
                    border: 2px solid #198754;
                    animation: fadeIn 0.3s ease;
                }
                
                .current-file-info {
                    border: 2px solid #6c757d;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .progress {
                    overflow: visible;
                }
                
                .progress-bar {
                    border-radius: 4px;
                    position: relative;
                }
                
                .badge {
                    padding: 0.5em 0.8em;
                    font-weight: 500;
                }
                
                @media (max-width: 768px) {
                    .file-dropzone {
                        padding: 2rem 1rem !important;
                    }
                    
                    .card-body {
                        padding: 1rem;
                    }
                    
                    .d-flex {
                        flex-direction: column;
                        gap: 10px;
                    }
                }
            `}</style>
        </>
    );
};

export default UpdateQuestion;